<script lang="ts" setup>
import { ref, onMounted, toRefs, computed } from 'vue';
import { PERMISSIONS, GRADES_ORDER } from '@/enum';
import { can } from '@/composables';
import EditGradesModal from '@/components/Grades/EditGradesModal.vue';
import { useGradeStore, PersonStores } from '@/store';

const props = defineProps<{
  organizationId: number;
}>();
const { organizationId } = toRefs(props);
const profileStore = PersonStores.useUserProfileStore();
const gradesStore = useGradeStore();
const toggleEditGradesModal = ref(false);
const person_id = ref();

onMounted(async () => {
  await init();
});

const init = async (): Promise<void> => {
  toggleEditGradesModal.value = false;
  await profileStore.getAuthUserProfile();
  person_id.value = profileStore.person?.id ?? 0;
  await gradesStore.fetchPersonGrades(person_id.value);
};

const sortedGrades = computed(() => {
  const filtered = gradesStore.personGrades.filter((grade) => grade.organization_id === organizationId.value);

  return filtered.sort((a, b) => {
    const indexA = GRADES_ORDER.findIndex((g) => g === a.grade);
    const indexB = GRADES_ORDER.findIndex((g) => g === b.grade);

    const safeIndexA = indexA === -1 ? GRADES_ORDER.length : indexA;
    const safeIndexB = indexB === -1 ? GRADES_ORDER.length : indexB;

    return safeIndexA - safeIndexB;
  });
});

const gradesRow = computed(() => {
  if (sortedGrades.value.length === 0) return [];
  return [
    {
      id: 1,
      grades: sortedGrades.value.map((g) => g.grade).join(', '),
    },
  ];
});

const openEditModal = () => {
  toggleEditGradesModal.value = true;
};
</script>

<template>
  <Card>
    <template #header>
      <h2 class="text-xl font-semibold">Grades</h2>
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.GRADE_ASSIGN])"
          type="text"
          size="small"
          text="Edit Grades"
          icon-left="add"
          @click="openEditModal"
        />
      </div>
    </template>

    <template #content>
      <DataTable :value="gradesRow" :loading="gradesStore.loading" data-key="id" responsive-layout="scroll">
        <template v-if="gradesRow.length > 0">
          <Column field="grades" header="Grades" />
        </template>
        <template #empty>All Grades.</template>
      </DataTable>
    </template>
  </Card>
  <EditGradesModal
    v-if="can([PERMISSIONS.GRADE_ASSIGN]) && sortedGrades && person_id"
    :visible="toggleEditGradesModal"
    :person-id="person_id"
    :organization-id="organizationId"
    :organization-name="null"
    :current-grades="sortedGrades"
    @cancel="toggleEditGradesModal = false"
    @sync="init"
  />
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
  .p-card-body {
    padding-bottom: 0px;
  }
}
</style>
